import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMessage } from '../contexts/MessageContext';
import { useQuery, useMutation } from 'react-query';
import api from '../services/api';

const GTMStrategy = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { showError } = useMessage();
    
    const [preferences, setPreferences] = useState({
        paymentTerm: 'All',
        businessModel: 'All',
        nmtRmt: 'All'
    });
    
    // Get FIT score results from navigation state or auto-calculation
    const [fitScoreResults, setFitScoreResults] = useState(location.state?.fitScoreResults);
    
    // Fetch brand products for FIT score calculation
    const { data: products } = useQuery(
        ['brand-products'],
        async () => {
            const response = await api.get('/discovery/products');
            return response.data.data;
        },
        {
            enabled: !!user && (user.role === 'brand_admin' || user.role === 'brand_user')
        }
    );

    // FIT Score calculation mutation
    const fitScoreMutation = useMutation(
        async () => {
            const response = await api.post('/fit-scores/calculate');
            return response.data;
        },
        {
            onSuccess: (data) => {
                setFitScoreResults(data.data);
            },
            onError: (error) => {
                showError(error.response?.data?.error || 'Failed to calculate FIT scores');
            }
        }
    );

    // Auto-calculate FIT scores when page loads and products are available
    useEffect(() => {
        if (user && products && products.length > 0 && !fitScoreResults && !fitScoreMutation.isLoading) {
            // Automatically trigger FIT score calculation
            fitScoreMutation.mutate();
        }
    }, [user, products, fitScoreResults, fitScoreMutation]);

    // Filter retailers based on preferences and sort by FIT score (descending)        
    const filteredRetailers = useMemo(() => {
        if (!fitScoreResults?.retailers) return [];

        // Log retailer data for debugging
        console.log('🖼️ Frontend Logo Debug - Raw retailer data:', fitScoreResults.retailers.map(r => ({
            name: r.retailer_name,
            logo_url: r.logo_url,
            logo_url_type: typeof r.logo_url,
            logo_url_empty: !r.logo_url
        })));

        // Filter retailers based on preferences
        let filtered = fitScoreResults.retailers.filter(retailer => {
            // Filter by Payment Term - Handle both "Outright" and "SOR( Sales or Return)" with case-insensitive matching
            if (preferences.paymentTerm !== 'All') {
                const selectedTerm = preferences.paymentTerm;
                const retailerTerm = retailer.purchase_model;
                
                if (selectedTerm === 'OUTRIGHT') {
                    // Match "Outright" (case-insensitive)
                    if (!retailerTerm || !retailerTerm.toLowerCase().includes('outright')) {
                        return false;
                    }
                } else if (selectedTerm === 'SOR') {
                    // Match "SOR" - check if it contains "SOR" (handles "SOR( Sales or Return)")
                    if (!retailerTerm || !retailerTerm.toUpperCase().includes('SOR')) {
                        return false;
                    }
                }
            }
            
            // Filter by Business Model
            if (preferences.businessModel !== 'All' && retailer.retailer_sale_model !== preferences.businessModel) {
                return false;
            }
            
            // Filter by NMT/RMT (using retailer_category)
            if (preferences.nmtRmt !== 'All') {
                if (preferences.nmtRmt === 'NMT' && !retailer.retailer_category?.includes('NMT')) {
                    return false;
                }
                if (preferences.nmtRmt === 'RMT' && !retailer.retailer_category?.includes('RMT')) {
                    return false;
                }
                if (preferences.nmtRmt === 'STANDALONE' && !retailer.retailer_category?.includes('STANDALONE')) {
                    return false;
                }
            }
            
            return true;
        });

        // Map and sort by FIT score in descending order
        return filtered
            .map(retailer => {
                // Log individual retailer logo data for debugging
                console.log(`🖼️ Frontend Logo Debug - Mapping ${retailer.retailer_name}:`, {
                    logo_url: retailer.logo_url,
                    logo_url_type: typeof retailer.logo_url,
                    logo_url_empty: !retailer.logo_url,
                    has_logo_url: 'logo_url' in retailer
                });

                return {
                    id: retailer.retailer_id,
                    name: retailer.retailer_name,
                    logo_url: retailer.logo_url, // Add logo_url to the mapped object
                    fitScore: retailer.fit_score,
                    outletCount: retailer.outlet_count || 0
                };
            })
            .sort((a, b) => b.fitScore - a.fitScore); // Sort by FIT score descending  
    }, [fitScoreResults, preferences]);

    const handlePreferenceChange = (type, value) => {
        setPreferences(prev => ({
            ...prev,
            [type]: value
        }));
    };


    const handleBackClick = () => {
        navigate('/discovery', {
            state: {
                activeTab: 'fit-scores',
                fitScoreResults
            }
        });
    };

    const preferenceOptions = {
        paymentTerm: ['All', 'OUTRIGHT', 'SOR'],
        businessModel: ['All', 'B2B', 'B2C'],
        nmtRmt: ['All', 'NMT', 'RMT', 'STANDALONE']
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-8 py-4">
                    <div className="flex items-center">
                        <button
                            onClick={handleBackClick}
                            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            <span className="text-sm font-medium">Back to Discovery</span>
                        </button>
                        <div className="ml-8">
                            <h1 className="text-2xl font-bold text-gray-900">Go To Market Strategy</h1>
                            <p className="text-sm text-gray-600 mt-1">Generate your retail partnership strategy</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-8 py-8">
                {/* Preference Selection - Matching Existing RetailVerse Design */}    
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">SELECT YOUR PREFERENCE</h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">       
                        {/* Payment Term */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">PAYMENT TERM</label>
                            <select
                                value={preferences.paymentTerm}
                                onChange={(e) => handlePreferenceChange('paymentTerm', e.target.value)}
                                className="input w-full"
                            >
                                {preferenceOptions.paymentTerm.map((option) => (       
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        {/* Business Model */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">BUSINESS MODEL</label>
                            <select
                                value={preferences.businessModel}
                                onChange={(e) => handlePreferenceChange('businessModel', e.target.value)}
                                className="input w-full"
                            >
                                {preferenceOptions.businessModel.map((option) => (     
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        {/* NATIONAL/REGIONAL MT */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">NATIONAL/REGIONAL MT</label>
                            <select
                                value={preferences.nmtRmt}
                                onChange={(e) => handlePreferenceChange('nmtRmt', e.target.value)}
                                className="input w-full"
                            >
                                {preferenceOptions.nmtRmt.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                </div>

                {/* Retailer Results - Show by default, update when preferences change */}
                {fitScoreResults && (
                    <div className="space-y-6">
                        {/* Strategy Title */}
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900">
                                RETAILER MATCH
                            </h2>
                        </div>

                        {/* No Matches Message */}
                        {filteredRetailers.length === 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                                <div className="text-yellow-600">
                                    <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Retailers Match Selected Criteria</h3>
                                    <p className="text-yellow-700">Please adjust your filter preferences to see matching retailers.</p>
                                </div>
                            </div>
                        )}

                        {/* Retailer Match Results - Matching Existing Design */}      
                        {filteredRetailers.length > 0 && (
                            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                                <div className="divide-y divide-gray-200">
                                    {filteredRetailers.map((retailer, index) => (
                                    <div key={retailer.id || index} className={`flex items-center justify-between p-4 ${
                                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'    
                                    }`}>
                                        {/* Retailer Logo and Name */}
                                        <div className="flex items-center space-x-4">  
                                            <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden bg-gray-100">
                                                {/* Log logo rendering for debugging */}
                                                {console.log(`🖼️ Frontend Logo Debug - Rendering ${retailer.name}:`, {
                                                    logo_url: retailer.logo_url,
                                                    logo_url_type: typeof retailer.logo_url,
                                                    logo_url_empty: !retailer.logo_url,
                                                    has_logo_url: 'logo_url' in retailer,
                                                    will_show_image: !!retailer.logo_url
                                                })}
                                                {retailer.logo_url ? (
                                                    <img 
                                                        src={retailer.logo_url} 
                                                        alt={`${retailer.name} logo`}
                                                        className="w-full h-full object-cover rounded-full"
                                                        onError={(e) => {
                                                            // Log image load error
                                                            console.log(`🖼️ Frontend Logo Debug - Image load error for ${retailer.name}:`, {
                                                                src: e.target.src,
                                                                error: 'Image failed to load'
                                                            });
                                                            // Fallback to initials if image fails to load
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                        onLoad={(e) => {
                                                            // Log successful image load
                                                            console.log(`🖼️ Frontend Logo Debug - Image loaded successfully for ${retailer.name}:`, {
                                                                src: e.target.src,
                                                                success: 'Image loaded successfully'
                                                            });
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className={`w-full h-full rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                                        index % 4 === 0 ? 'bg-red-500' :       
                                                        index % 4 === 1 ? 'bg-blue-500' :      
                                                        index % 4 === 2 ? 'bg-orange-500' :    
                                                        'bg-purple-500'
                                                    }`}
                                                    style={{ display: retailer.logo_url ? 'none' : 'flex' }}
                                                >
                                                    {retailer.name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2)}
                                                </div>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900">{retailer.name}</h3>
                                            </div>
                                        </div>

                                        {/* Match Score */}
                                        <div className="text-right">
                                            <div className={`text-2xl font-bold ${     
                                                (retailer.matchScore || retailer.fitScore) >= 80 ? 'text-green-600' :
                                                (retailer.matchScore || retailer.fitScore) >= 60 ? 'text-yellow-600' :
                                                'text-red-600'
                                            }`}>
                                                {retailer.matchScore || retailer.fitScore}%
                                            </div>
                                        </div>
                                    </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Loading State */}
                {fitScoreMutation.isLoading && (
                    <div className="text-center py-12">
                        <div className="bg-gray-50 rounded-lg p-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Calculating FIT Scores...</h3>
                            <p className="text-gray-600">Please wait while we analyze your products and calculate FIT scores for GTM strategy.</p>
                        </div>
                    </div>
                )}

                {/* No Products State */}
                {!fitScoreMutation.isLoading && (!products || products.length === 0) && (
                    <div className="text-center py-12">
                        <div className="bg-gray-50 rounded-lg p-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
                            <p className="text-gray-600 mb-4">Please add some products first to calculate FIT scores and generate GTM strategy.</p>
                            <button
                                onClick={() => navigate('/product-management')}
                                className="btn-primary"
                            >
                                Add Products
                            </button>
                        </div>
                    </div>
                )}

                {/* No FIT Score Results State */}
                {!fitScoreMutation.isLoading && products && products.length > 0 && !fitScoreResults && (
                    <div className="text-center py-12">
                        <div className="bg-gray-50 rounded-lg p-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Calculating FIT Scores...</h3>
                            <p className="text-gray-600 mb-4">Preparing your GTM strategy based on your products.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GTMStrategy;
